import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import AlertDialog from "./AlertDialog";
import ConfirmDialog from "./ConfirmDialog";

const DialogContext = createContext(null);

const EMPTY = { kind: null, open: false, title: "", message: "", severity: undefined };

/**
 * App-wide imperative replacement for window.confirm / window.alert.
 *
 *   const { confirm, alert } = useDialogs();
 *   if (!(await confirm({ title: "Belgeyi Sil", message: "Emin misiniz?" }))) return;
 *   await alert({ title: "Hata", message: "...", severity: "error" });
 *
 * Renders the shared AlertDialog / ConfirmDialog so every confirmation and
 * alert in the app looks identical.
 */
function DialogProvider({ children }) {
  const [state, setState] = useState(EMPTY);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((prev) => ({ ...prev, open: false }));
    if (resolve) resolve(value);
  }, []);

  const open = useCallback((kind, options) => {
    const opts = typeof options === "string" ? { message: options } : options || {};
    return new Promise((resolve) => {
      // A second call while one is pending resolves the first with a safe "no".
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setState({
        kind,
        open: true,
        title: opts.title || (kind === "confirm" ? "Onay" : "Bilgi"),
        message: opts.message || "",
        severity: opts.severity,
        confirmText: opts.confirmText,
        cancelText: opts.cancelText,
        buttonText: opts.buttonText,
      });
    });
  }, []);

  const api = useMemo(
    () => ({
      confirm: (options) => open("confirm", options),
      alert: (options) => open("alert", options),
    }),
    [open]
  );

  return (
    <DialogContext.Provider value={api}>
      {children}
      {state.kind === "confirm" && (
        <ConfirmDialog
          open={state.open}
          title={state.title}
          message={state.message}
          severity={state.severity}
          confirmText={state.confirmText}
          cancelText={state.cancelText}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
      {state.kind === "alert" && (
        <AlertDialog
          open={state.open}
          title={state.title}
          message={state.message}
          severity={state.severity}
          buttonText={state.buttonText}
          onClose={() => settle(undefined)}
        />
      )}
    </DialogContext.Provider>
  );
}

DialogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useDialogs() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialogs must be used inside a DialogProvider");
  return ctx;
}

export { useDialogs };
export default DialogProvider;
